import { ethers } from 'ethers';
import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import { Flexbox, FlexboxColumn } from '../../components/action/Flexbox';
import { Line } from '../../components/action/Line';
import SearchBar from '../../components/action/SearchBar';
import Logo from '../../components/Logo/Logo';
import ThemedText from '../../components/theme/ThemedText';
import { useBackendApi } from '../../helpers/backendHelper';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { trackClairtyEvent, truncateEthereumAddress } from '../../utils';
import EnsAvatars from './EnsData';
import './Profiles.scss';

export const TopTakesRowSkeleton = () => {
  // Define custom skeleton colors
  const baseColor = 'var(--skeleton-base-color)';
  const highlightColor = 'var(--skeleton-highlight-color)';

  return (
    <FlexboxColumn alignItems='flex-start' gap='0.5rem'>
      <Flexbox gap='0.25rem'>
        <Skeleton circle width='1rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
        <Skeleton width='6rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
      </Flexbox>
      <Flexbox gap='0.5rem'>
        <Skeleton width='4rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
        <Skeleton width='6rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
      </Flexbox>
    </FlexboxColumn>
  );
};
export const EnsRowSkelton = () => {
  // Define custom skeleton colors
  const baseColor = 'var(--skeleton-base-color)';
  const highlightColor = 'var(--skeleton-highlight-color)';

  return (
    <FlexboxColumn alignItems='flex-start' gap='0.5rem'>
      <Flexbox gap='0.25rem'>
        <Skeleton circle width='1rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
        <Skeleton width='6rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
      </Flexbox>
      <Flexbox gap='0.5rem'>
        <Skeleton width='16rem' height='1rem' baseColor={baseColor} highlightColor={highlightColor} />
      </Flexbox>
    </FlexboxColumn>
  );
};

export function TopTakesRow({ data, onClick }) {
  return (
    <FlexboxColumn className='pointer' alignItems='flex-start' gap='0.5rem' onClick={() => onClick(data)}>
      <Flexbox gap='0.25rem'>
        <Logo width={'1rem'} height={'1rem'} borderRadius='50%' src={data?.avatarurl} />
        <ThemedText.H6 weight='semi-bold'>{data?.name}</ThemedText.H6>
      </Flexbox>
      <Flexbox gap='0.5rem'>
        <ThemedText.H6>
          {data?.posts || 0} <ThemedText.Span color='color-xlight'>Takes</ThemedText.Span>
        </ThemedText.H6>
        <ThemedText.H6>
          {data?.engagements || 0} <ThemedText.Span color='color-xlight'>Engagements</ThemedText.Span>
        </ThemedText.H6>
      </Flexbox>
    </FlexboxColumn>
  );
}

const Profiles = () => {
  const navigate = useNavigate();
  const backendHelper = useBackendApi();
  const [{ user }] = useGlobalState();

  const [topTakers, setTopTakers] = useState();
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState();
  const [searchResult, setSearchResult] = useState();

  const getTopTakers = useCallback(async () => {
    try {
      const response = await backendHelper.reviews.getTopTakers();
      setTopTakers(response.data?.slice(0, 10));
    } catch (error) {
      console.log('Error is getting top takes', error);
    }
  }, [backendHelper]);

  const updateRecentProfiles = profile => {
    const existing = JSON.parse(localStorage.getItem('recentProfiles')) || [];

    const filtered = existing.filter(item => item.address !== profile.address);
    const updated = [profile, ...filtered].slice(0, 5); // limit to 5 recent

    localStorage.setItem('recentProfiles', JSON.stringify(updated));
    setRecentProfiles(updated);
  };

  function handleRedirect(profile) {
    if (!profile?.address) return;
    updateRecentProfiles(profile);
    navigate(`/profile/${profile?.address}/projects`);
  }

  function updateSearch(value) {
    setSearchTerm(value);
    if (ethers.isAddress(value)) {
      setSearchResult([{ name: truncateEthereumAddress(value), address: value }]);
    } else {
      setSearchResult([]);
    }
  }

  function handleSearchClick() {
    if (ethers.isAddress(searchTerm)) {
      setSearchTerm('');
      setSearchResult(null);
      
      trackClairtyEvent('user_profile_visited', {
        origin: 'profiles_search',
        is_own_profile: Boolean(searchTerm === user?.address),
      });
      navigate(`/profile/${searchTerm}/projects`);

      updateRecentProfiles({ address: searchTerm });
    }
  }

  function handleSearch(text) {
    if (!text) return;
    if (ethers.isAddress(text)) {
      setSearchTerm('');

      navigate(`/profile/${text}/projects`);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && searchTerm.trim() !== '') {
      handleSearch(searchTerm);
      setSearchResult(null);
    }
  }

  useEffect(() => {
    getTopTakers();
    const storedRecent = JSON.parse(localStorage.getItem('recentProfiles')) || [];
    setRecentProfiles(storedRecent);
  }, []);

  return (
    <FlexboxColumn fw alignItems='flex-start'>
      <FlexboxColumn fw alignItems='flex-start' className='profiles_wrapper'>
        <SearchBar
          className='profiles_wrapper_searchbar'
          placeholder='Search any EVM address'
          gap='1rem'
          value={searchTerm}
          onChange={e => updateSearch(e.target.value)}
          handleKeyDown={handleKeyDown}
          searchData={searchResult}
          handleSearchClick={handleSearchClick}
        />
        <Flexbox gap='2rem'>
          <ThemedText.H6 weight='semi-bold'>Recent</ThemedText.H6>{' '}
          <Flexbox className='1.5rem'>
            {recentProfiles.length === 0 && <ThemedText.H6 color='color-xlight'>No recent profiles</ThemedText.H6>}
            {recentProfiles.map(profile => (
              <ThemedText.H6 color='color-xlight' className='pointer' key={profile.address} onClick={() => handleRedirect(profile)}>
                {profile?.name || truncateEthereumAddress(profile?.address)}
              </ThemedText.H6>
            ))}
          </Flexbox>
        </Flexbox>
      </FlexboxColumn>

      <Line />
      <Flexbox fw gap='10%' justifyContent='flex-start' alignItems='flex-start' className='profiles_wrapper'>
        <FlexboxColumn alignItems='flex-start' gap='1.5rem'>
          <ThemedText.H5 weight='semi-bold'>Top Takers</ThemedText.H5>
          {!topTakers && Array.from({ length: 5 }).map(() => <TopTakesRowSkeleton />)}
          {topTakers?.map(taker => (
            <TopTakesRow onClick={handleRedirect} data={taker} />
          ))}
        </FlexboxColumn>
        {!topTakers ? (
          <FlexboxColumn alignItems='flex-start' gap='1.5rem'>
            <ThemedText.H5 weight='semi-bold'>Top ENS</ThemedText.H5>
            {!topTakers && Array.from({ length: 5 }).map(() => <EnsRowSkelton />)}
          </FlexboxColumn>
        ) : (
          <EnsAvatars handleRedirect={handleRedirect} />
        )}
      </Flexbox>
    </FlexboxColumn>
  );
};

export default Profiles;
